import { useState } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Send, Building2, Phone, FileText, Truck, UserCheck, Camera, CreditCard, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BulkInquiry() {
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    gst_number: '',
    phone: '',
    email: '',
    estimated_quantity: '',
    timeline: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTimelineChange = (value) => {
    setFormData(prev => ({ ...prev, timeline: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.full_name || !formData.company_name || !formData.phone || !formData.email || !formData.estimated_quantity || !formData.timeline) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/bulk-inquiry`, formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully! Our team will contact you within 2 hours.');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen noise-texture">
        <SEO
          title="Bulk Inquiry Submitted | Modular Furniture Store"
          description="Thank you for your bulk inquiry. Our team will contact you shortly."
        />
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-black uppercase mb-4">Inquiry Received!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your bulk inquiry. Our dedicated B2B team will contact you within <span className="font-bold text-primary">2 hours</span> with a personalized quote.
            </p>
            <div className="bg-primary/5 border-2 border-primary/20 p-6 rounded-lg mb-8">
              <p className="text-sm text-muted-foreground">
                <strong>Need immediate assistance?</strong><br />
                Call us directly at <a href="tel:+919594685818" className="text-primary font-bold">+91-95946-85818</a> or WhatsApp us.
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="rounded-none uppercase tracking-wider font-bold">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/products">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold">
                  Browse Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-texture">
      <SEO
        title="Bulk Inquiry - Get Custom Quote | Modular Furniture Store"
        description="Get personalized bulk pricing, GST-compliant proforma invoices, and dedicated account manager for your office furniture needs."
        keywords="bulk order, office furniture quote, GST invoice, corporate furniture, wholesale workstations"
      />
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-4">
            Need a Custom Quote for Your Office?
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Planning a larger space? Get a personalized quote, GST-compliant proforma invoices, and bulk pricing in under <span className="font-bold text-primary">2 hours</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white border-2 border-border p-8">
              <h2 className="text-2xl font-black uppercase mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                Bulk Inquiry Form
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="uppercase text-xs tracking-wider font-bold">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="rounded-none border-2"
                    required
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="uppercase text-xs tracking-wider font-bold">
                    Company Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Acme Pvt. Ltd."
                    className="rounded-none border-2"
                    required
                  />
                </div>

                {/* GST Number */}
                <div className="space-y-2">
                  <Label htmlFor="gst_number" className="uppercase text-xs tracking-wider font-bold">
                    GST Number <span className="text-muted-foreground">(Optional)</span>
                  </Label>
                  <Input
                    id="gst_number"
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    placeholder="27AABCU9603R1ZM"
                    className="rounded-none border-2"
                  />
                  <p className="text-xs text-muted-foreground">For GST input credit benefits</p>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="uppercase text-xs tracking-wider font-bold">
                    Work Phone / WhatsApp <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="rounded-none border-2"
                    required
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="uppercase text-xs tracking-wider font-bold">
                    Work Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="rounded-none border-2"
                    required
                  />
                </div>

                {/* Estimated Quantity */}
                <div className="space-y-2">
                  <Label htmlFor="estimated_quantity" className="uppercase text-xs tracking-wider font-bold">
                    Estimated Quantity <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="estimated_quantity"
                    name="estimated_quantity"
                    value={formData.estimated_quantity}
                    onChange={handleChange}
                    placeholder="e.g., 20 workstations, 5 cabinets"
                    className="rounded-none border-2"
                    required
                  />
                </div>

                {/* Timeline */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="uppercase text-xs tracking-wider font-bold">
                    Requirement Timeline <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.timeline} onValueChange={handleTimelineChange} required>
                    <SelectTrigger className="rounded-none border-2">
                      <SelectValue placeholder="Select timeline..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediate (Within 7 days)</SelectItem>
                      <SelectItem value="15_days">Within 15 Days</SelectItem>
                      <SelectItem value="1_month">Within 1 Month</SelectItem>
                      <SelectItem value="1_month_plus">1 Month+</SelectItem>
                      <SelectItem value="planning">Just Planning / Exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="message" className="uppercase text-xs tracking-wider font-bold">
                    Message / Special Requirements
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your office setup - size, layout preferences, color choices, any specific requirements..."
                    className="rounded-none border-2 min-h-[120px]"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold py-6 text-lg"
                >
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Get Custom Quote
                    </>
                  )}
                </Button>
                <p className="text-center text-sm text-muted-foreground mt-3">
                  We typically respond within 2 hours during business hours (Mon-Sat, 9 AM - 6 PM)
                </p>
              </div>
            </form>
          </div>

          {/* Trust Box - B2B Perks */}
          <div className="space-y-6">
            {/* Why Order Bulk */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/20 p-6">
              <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Why Order Bulk with Us?
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">Direct GST Invoicing</p>
                    <p className="text-xs text-muted-foreground">Save up to 18% with valid GST input credit</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">Dedicated Account Manager</p>
                    <p className="text-xs text-muted-foreground">One point of contact from payment to installation</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Camera className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">Pre-Dispatch Verification</p>
                    <p className="text-xs text-muted-foreground">Actual pics/videos of products before dispatch</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">Pan-India Shipping</p>
                    <p className="text-xs text-muted-foreground">Special logistics for multi-city office setups</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-[#f8f7f4] border border-gray-200 p-6">
              <h3 className="font-bold mb-3 text-[#1a1a1a]">Prefer to Talk?</h3>
              <p className="text-sm text-gray-600 mb-4">Our B2B team is ready to help</p>
              <a 
                href="tel:+919594685818"
                className="flex items-center gap-2 bg-[#1a1a1a] text-white font-bold px-4 py-3 hover:bg-[#2a2a2a] transition-colors"
              >
                <Phone className="w-5 h-5" />
                +91-95946-85818
              </a>
              <a 
                href="https://wa.me/919594685818?text=Hi%2C%20I%20need%20a%20bulk%20quote%20for%20office%20furniture"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white font-bold px-4 py-3 mt-2 hover:bg-[#128C7E] transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>

            {/* Trust Badges */}
            <div className="bg-white border-2 border-border p-4">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modshop-seo/artifacts/us9gplcj_image.png"
                  alt="Ezeefit"
                  className="h-10 w-auto"
                />
                <div className="text-xs">
                  <p className="font-bold">A Division of Ezeefit</p>
                  <p className="text-muted-foreground">10+ Years of Trust</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ISO 9001:2015 & IGBC Certified | 5000+ Offices Furnished
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="bulkInquiry" />
        <RelatedPages pageKey="bulkInquiry" className="mt-12" />
      </div>
    </div>
  );
}
